import { motion } from 'motion/react';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../../../../routes/paths'; // Adjusted import path
import { useApps } from '../../../../pages/account-management/apps/hooks/use-apps'; // Corrected path
import { useCrmAccounts } from '../../../../pages/account-management/apps/hooks/use-crm-accounts'; // Corrected path
import { cn } from '@shadcn/ui/lib/utils';
import { CheckIcon, Link } from 'lucide-react';
import { useEffect, useState } from 'react';

// Add an 'id' to match hook data keys
const integrationDetails = [
    { name: 'LinkedIn', imgSrc: '/images/integrations/linkedin-logo.png', id: 'linkedin' },
    { name: 'Gmail', imgSrc: '/images/integrations/gmail-logo.png', id: 'gmail' },
    { name: 'Outlook', imgSrc: '/images/integrations/outlook-logo.png', id: 'outlook' },
    { name: 'HubSpot', imgSrc: '/images/integrations/hubspot-logo.png', id: 'hubspot' },
    { name: 'Salesforce', imgSrc: '/images/integrations/Salesforce.com_logo.png', id: 'salesforce' },
    { name: 'Pipedrive', imgSrc: '/images/integrations/pipedrive-logo-300.png', id: 'pipedrive' },
];

const Integrations = () => {
    const navigate = useNavigate();
    const { accounts: appAccounts, isLoading: isLoadingApps } = useApps();
    const { accounts: crmAccounts, isLoading: isLoadingCrms } = useCrmAccounts();
    const [screenHeight, setScreenHeight] = useState(window.innerHeight);

    useEffect(() => {
        window.addEventListener('resize', () => {
            setScreenHeight(window.innerHeight);
        });
        return () => {
            window.removeEventListener('resize', () => {
                setScreenHeight(window.innerHeight);
            });
        };
    }, []);

    const handleIntegrationClick = () => {
        navigate(`${paths.NEW.ACCOUNT_MANAGEMENT_PATHS.ROOT}/${paths.NEW.ACCOUNT_MANAGEMENT_PATHS.APPS}`);
    };

    if (isLoadingApps || isLoadingCrms) {
        // Still rendering with defaults if loading, hooks will update opacity once loaded
    }

    return (
        <div className="flex w-full lg:h-32 h-24 items-start justify-center overflow-hidden animate-fade-in-0.5">
            {integrationDetails.map((integration, index) => {
                const numIcons = integrationDetails.length;
                // Spread icons over an ~800px width, centered. (Icon width ~80px on md)
                // (index / (numIcons - 1)) goes from 0 to 1.
                // So, range is from -400 to +400.
                const baseX = (index / (numIcons - 1)) * 500 - 250 + (Math.random() * 20 - 10);
                const baseY = Math.random() * 10 + window.innerHeight * 0.01; // Vertical spread in the upper part

                const xSwayAmount = Math.random() * 1; // User wants no X sway

                // Calculate sway factor: 1 for center, 0 for edges
                const normalizedPosition = numIcons > 1 ? index / (numIcons - 1) : 0.5; // 0 to 1
                const distanceFromCenter = Math.abs(normalizedPosition - 0.5); // 0 (center) to 0.5 (edges)
                const swayFactor = 1 - distanceFromCenter * 2; // 1 (center) to 0 (edges)

                // Min sway for edges: 5px. Max additional sway for center: 25px.
                // So, center sways 5 + (something between 0-25) = 5px to 30px
                // Edges sway 5 + (0 * random) = 5px
                const ySwayAmount = 5 + swayFactor * 10;

                let isConnected = false;
                if (appAccounts) {
                    if (integration.id === 'linkedin' && appAccounts.linkedin?.isConnected) isConnected = true;
                    if (integration.id === 'gmail' && appAccounts.gmail?.isConnected) isConnected = true;
                    if (integration.id === 'outlook' && appAccounts.outlook?.isConnected) isConnected = true;
                }
                if (crmAccounts) {
                    if (integration.id === 'hubspot' && crmAccounts.hubspot) isConnected = true;
                    if (integration.id === 'salesforce' && crmAccounts.salesforce) isConnected = true;
                    if (integration.id === 'pipedrive' && crmAccounts.pipedrive) isConnected = true;
                }

                const restingOpacity = isConnected ? 1 : 0.5;

                return (
                    <motion.div // Outer div for position and click
                        key={integration.name}
                        className="absolute m-2 cursor-pointer flex flex-row items-end" // Minimal classes for positioning
                        onClick={handleIntegrationClick}
                        initial={{
                            x: baseX,
                            y: baseY,
                        }}
                        animate={{
                            x: [baseX, baseX + xSwayAmount, baseX, baseX - xSwayAmount, baseX],
                            y: [baseY, baseY + ySwayAmount, baseY, baseY - ySwayAmount, baseY],
                            rotate: [0, Math.random() * 10 - 5, 0, Math.random() * -10 + 5, 0],
                        }}
                        transition={{
                            duration: 10 + ySwayAmount,
                            delay: index * 1,
                            repeat: Infinity,
                            ease: 'linear',
                        }}
                    >
                        <motion.div // Inner div for appearance, opacity, scale, and hover effects
                            className="flex h-12 w-12 items-end justify-center rounded-full bg-white p-1 shadow-lg md:h-14 md:w-14"
                            initial={{
                                opacity: restingOpacity,
                                scale: 1,
                            }}
                            animate={{
                                // Ensures opacity updates if connection status changes dynamically
                                opacity: restingOpacity,
                                scale: 1,
                            }}
                            whileHover={{
                                opacity: 1,
                                scale: 1.1,
                                transition: { duration: 0.25, ease: 'easeInOut' },
                            }}
                        >
                            <img
                                src={integration.imgSrc}
                                alt={integration.name}
                                className={cn(
                                    'h-full w-full object-contain',
                                    integration.id === 'outlook' && 'scale-[0.7]',
                                    integration.id === 'gmail' && 'scale-[0.7]',
                                    integration.id === 'hubspot' && 'scale-[0.8]',
                                    integration.id === 'salesforce' && 'scale-[0.8]',
                                    integration.id === 'pipedrive' && 'scale-[0.8]'
                                )}
                            />
                            {isConnected && (
                                <motion.div className="absolute bottom-0 -right-1 rounded-full border-success-200 bg-success-50 color-success-700 text-success-700 dark:bg-success-50/20 dark:text-primary-foreground w-5 h-5 flex items-center justify-center -ml-1 shadow-lg z-30">
                                    <Link className="w-3 h-3" />
                                </motion.div>
                            )}
                        </motion.div>
                    </motion.div>
                );
            })}
        </div>
    );
};

export default Integrations;
